import { cn } from "@repo/design-system/utils";
import { Fragment, type ReactNode } from "react";

/**
 * Renders an answer's Markdown subset as React elements: paragraphs, headings
 * (as bold lines), bullet and numbered lists, **bold**, *italic*, `code` and
 * line breaks. Everything else is shown as plain text.
 *
 * The model's text only ever becomes React text nodes - never HTML - so there is
 * nothing to inject. Links are reduced to their label: an answer must not send
 * a salon off to a site we have not vetted, and the supplier detail view makes
 * the same promise. The repo has no Markdown renderer, and pulling one in for
 * this subset would also pull in HTML and URL handling that we do not want.
 */
export function AssistantMarkdown({
  text,
  className
}: {
  text: string;
  className?: string;
}) {
  const blocks = parseBlocks(text);
  return (
    <div className={cn("space-y-2 break-words", className)}>
      {blocks.map((block, index) => {
        switch (block.kind) {
          case "heading":
            return (
              <p key={index} className="font-semibold">
                {renderInline(block.text)}
              </p>
            );
          case "ul":
            return (
              <ul key={index} className="list-disc space-y-1 pl-5">
                {block.items.map((item, i) => (
                  <li key={i}>{renderInline(item)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={index} className="list-decimal space-y-1 pl-5">
                {block.items.map((item, i) => (
                  <li key={i}>{renderInline(item)}</li>
                ))}
              </ol>
            );
          case "p":
            return (
              <p key={index}>
                {block.lines.map((line, i) => (
                  <Fragment key={i}>
                    {i > 0 && <br />}
                    {renderInline(line)}
                  </Fragment>
                ))}
              </p>
            );
        }
      })}
    </div>
  );
}

export type Block =
  | { kind: "heading"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "p"; lines: string[] };

const HEADING = /^#{1,6}\s+(.*)$/;
const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flush = () => {
    if (paragraph.length > 0) blocks.push({ kind: "p", lines: paragraph });
    paragraph = [];
  };

  for (const line of text.replace(/\r\n?/g, "\n").split("\n")) {
    if (line.trim() === "") {
      flush();
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: "heading", text: heading[1] });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    const item = bullet ?? numbered;
    if (item) {
      flush();
      const kind = bullet ? "ul" : "ol";
      const last = blocks[blocks.length - 1];
      if (last && last.kind === kind) last.items.push(item[1]);
      else blocks.push({ kind, items: [item[1]] });
      continue;
    }
    paragraph.push(line);
  }
  flush();
  return blocks;
}

// `code`, **bold**, *italic* / _italic_, [label](target). Alternation order
// matters: ** before *.
const INLINE =
  /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*\s][^*]*)\*|\b_([^_\s][^_]*)_\b|\[([^\]]+)\]\([^)]*\)/g;

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE)) {
    const start = match.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    const [, code, bold, star, underscore, label] = match;
    if (code !== undefined) {
      out.push(
        <code
          key={key++}
          className="bg-ui-bg-subtle-pressed rounded px-1 font-mono text-xs"
        >
          {code}
        </code>
      );
    } else if (bold !== undefined) {
      out.push(<strong key={key++}>{renderInline(bold)}</strong>);
    } else if (star !== undefined || underscore !== undefined) {
      out.push(<em key={key++}>{star ?? underscore}</em>);
    } else if (label !== undefined) {
      out.push(label);
    }
    last = start + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
